import {
  styled,
  Table,
  TableCell,
  TableCellProps,
  TableContainer,
  Typography,
  TypographyProps,
} from "@mui/material";
import { alpha } from "@mui/material";
import { Glass } from "Components/Glass";
import React, { useCallback } from "react";
import SpacedListContainer from "Components/UI/SpacedListContainer";

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  width: `calc(100% + 20px)`,
  padding: `0 10px`,
  marginLeft: -10,
}));

const StyledTable = styled(Table)(({ theme }) => ({
  borderCollapse: "separate",
  borderStyle: "hidden",
  "& .dragging": {
    "& .MuiTableRow-root": {
      "&:nth-last-child(2)": {
        "& .MuiTableCell-root": {
          borderBottom: 0,

          "&:first-child": {
            borderBottomLeftRadius: theme.shape.borderRadius,
            "&::before": {
              borderBottomLeftRadius: theme.shape.borderRadius,
            },
          },
          "&:last-child": {
            borderBottomRightRadius: theme.shape.borderRadius,
            "&::before": {
              borderBottomRightRadius: theme.shape.borderRadius,
            },
          },
        },
      },
    },
  },
  "& .dropping": {
    "& .MuiTableRow-root": {
      transition: "none",
    },
  },

  "& .MuiTableCell-root": {
    maxWidth: 450,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    color: theme.palette.primary.dark,
    borderBottom: `${theme.spacing(1)} solid transparent`,
    borderRight: `1px solid transparent`,
    position: "relative",
    background: alpha(theme.palette.common.white, 0.7),
    backgroundClip: "padding-box",
    "&::before": {
      position: "absolute",
      content: "''",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      zIndex: -1,
      boxShadow: "2px 2px 5px #6B85B319",
    },
  },

  "& .MuiTableRow-head": {
    overflow: "hidden",
    "& .MuiTableCell-root": {
      background: theme.palette.primary.main,
      backgroundClip: "padding-box",
      fontSize: theme.typography.body1.fontSize,
      fontWeight: 600,

      "&:first-child": {
        borderTopLeftRadius: theme.shape.borderRadius,
        "&::before": {
          borderTopLeftRadius: theme.shape.borderRadius,
        },
      },
      "&:last-child": {
        borderTopRightRadius: theme.shape.borderRadius,
        "&::before": {
          borderTopRightRadius: theme.shape.borderRadius,
        },
      },
    },
  },
  "& .MuiTableCell-head": {
    color: theme.palette.common.white,
  },
  "& .MuiTableBody-root": {
    position: "relative",
    overflow: "hidden",
    "& .MuiTableRow-root": {
      left: "auto!important",
      top: "auto!important",

      "&.dragging": {
        transition: "none",
        borderBottom: `${theme.spacing(1)} solid ${theme.palette.secondary.light}`,
      },

      "&:last-child": {
        "& .MuiTableCell-root": {
          borderBottom: 0,

          "&:first-child": {
            borderBottomLeftRadius: theme.shape.borderRadius,
            "&::before": {
              borderBottomLeftRadius: theme.shape.borderRadius,
            },
          },
          "&:last-child": {
            borderBottomRightRadius: theme.shape.borderRadius,
            "&::before": {
              borderBottomRightRadius: theme.shape.borderRadius,
            },
          },
        },
      },
      transition: "transform 0.17s",
      "&:hover": {
        transform: `scale(1.007)`,
      },
    },
  },
}));

interface ISpacedCellProps extends TableCellProps {}
export const SpacedCell = styled(TableCell)<ISpacedCellProps>(
  ({ theme }) => ({}),
);

const BaseSpacedLinkButton = ({
  color = "primary",
  variant = "subtitle2",
  ...rest
}: TypographyProps) => {
  return <Typography color={color} variant={variant} {...rest} />;
};

export const SpacedLinkButton = styled(BaseSpacedLinkButton)(({ theme }) => ({
  position: "relative",
  display: "inline-block",

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 2,
    left: 0,
    width: "0%",
    height: 1,
    background: alpha(theme.palette.primary.main, 0.5),
    transition: "all 0.2s",
  },
  "&:hover": {
    cursor: "pointer",
    "&::after": {
      width: "100%",
      height: 1,
      background: theme.palette.primary.main,
    },
  },
}));

interface ISpacedListProps extends Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "title"
> {
  title?: string | React.ReactNode;
  subTitle?: string;
  action?: React.ReactNode;
  isEmpty?: boolean;
  height?: number | string;
}

function SpacedList({
  isEmpty,
  title,
  subTitle,
  action,
  children,
  height,
  ...rest
}: ISpacedListProps) {
  const renderBody = useCallback(
    () => (
      <StyledTableContainer>
        <StyledTable {...rest}>{children}</StyledTable>
        {isEmpty && (
          <Glass
            noShadow
            sx={{
              minHeight: 100,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography color={"secondary"} variant="subtitle2">
              No Data
            </Typography>
          </Glass>
        )}
      </StyledTableContainer>
    ),
    [children, isEmpty, rest],
  );

  return (
    <SpacedListContainer
      title={title}
      subTitle={subTitle}
      action={action}
      height={height}
    >
      <>{renderBody()}</>
    </SpacedListContainer>
  );
}

export default SpacedList;
